import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { badgeClass } from "@/lib/config";

export function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return v;
  return "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
export function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
export function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function cellValue(row, col) {
  const v = row[col.key];
  if (col.money) return fmtMoney(v);
  if (col.date) return fmtDate(v);
  if (col.datetime) return fmtDateTime(v);
  return v === null || v === undefined || v === "" ? "—" : String(v);
}

export default function DataTable({ columns, rows, total, page, pageSize, onPage, onSearch, searchValue, onRowClick, loading, actions }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 25)));
  return (
    <div className="border border-border rounded-sm bg-white">
      {onSearch && (
        <div className="p-3 border-b border-border flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-mutedsea" />
            <Input data-testid="table-search" placeholder="Search…" className="pl-8 h-9 text-sm bg-white"
              value={searchValue} onChange={(e) => onSearch(e.target.value)} />
          </div>
          <span className="text-xs text-mutedsea ml-auto">{total} record{total === 1 ? "" : "s"}</span>
        </div>
      )}
      <div className="overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cloud border-b border-border">
              {columns.map((c) => (
                <th key={c.key} className="text-left font-semibold text-navy px-3 py-2 whitespace-nowrap text-xs uppercase tracking-wide">{c.label}</th>
              ))}
              {actions && <th className="text-right font-semibold text-navy px-3 py-2 text-xs uppercase tracking-wide">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="px-3 py-8 text-center text-mutedsea">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="px-3 py-8 text-center text-mutedsea">No records found.</td></tr>
            ) : rows.map((row, i) => (
              <tr key={row.id || i} data-testid={`row-${row.id || i}`}
                className={`border-b border-border last:border-0 hover:bg-ice ${onRowClick ? "cursor-pointer" : ""}`}
                onClick={() => onRowClick && onRowClick(row)}>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 whitespace-nowrap">
                    {c.badge ? (
                      row[c.key] ? <Badge variant="outline" className={`font-normal ${badgeClass(row[c.key])}`}>{row[c.key]}</Badge> : "—"
                    ) : cellValue(row, c)}
                  </td>
                ))}
                {actions && (
                  <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {onPage && total > pageSize && (
        <div className="flex items-center justify-between p-3 border-t border-border">
          <span className="text-xs text-mutedsea">Page {page} of {totalPages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
